import React, { useState, useEffect } from "react";
import { X, Truck } from "lucide-react";

const CheckoutForm = ({ cartUuid, onClose, isOrderSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    addressDetail: "",
    zipCode: "",
    memo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success] = useState(isOrderSuccess || false);
  const [cartDetails, setCartDetails] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load Daum Postcode script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch user information
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) {
          setError("인증 정보가 없습니다. 다시 로그인해주세요.");
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/me`, {
          headers: {
            Authorization: token,
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setFormData((prevData) => ({
            ...prevData,
            name: userData.name || "",
            phone: userData.phone || "",
            email: userData.email || "",
            address: userData.address || "",
            addressDetail: userData.addressDetail || "",
            zipCode: userData.zipCode || "",
          }));
        } else {
          setError("사용자 정보를 불러오는데 실패했습니다.");
        }
      } catch (err) {
        console.error("사용자 정보 조회 오류:", err);
        setError("서버 연결에 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, []);

  // Fetch cart details
  useEffect(() => {
    const fetchCartDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/cart/${cartUuid}`, {
          headers: {
            Authorization: localStorage.getItem("auth_token"),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCartDetails(data);
        } else {
          setError("장바구니 정보를 불러오는데 실패했습니다.");
        }
      } catch (err) {
        console.error("장바구니 정보 조회 오류:", err);
        setError("서버 연결에 실패했습니다.");
      }
    };

    if (cartUuid) {
      fetchCartDetails();
    }
  }, [cartUuid]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle address search
  const handleAddressSearch = () => {
    if (window.daum && window.daum.Postcode) {
      new window.daum.Postcode({
        oncomplete: function (data) {
          setFormData({
            ...formData,
            zipCode: data.zonecode,
            address: data.address,
            addressDetail: data.buildingName ? ` (${data.buildingName})` : ` (${data.bname2})`,
          });
        },
      }).open();
    } else {
      setError("주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
    }
  };

  // Validate form data
  const validateForm = () => {
    const requiredFields = {
      name: "이름",
      phone: "연락처",
      email: "이메일",
      zipCode: "우편번호",
      address: "주소",
      addressDetail: "상세주소",
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        setError(`${label}을(를) 입력해주세요.`);
        return false;
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Update user information - only name and email in form-encoded format
      const formParams = new URLSearchParams();
      formParams.append("name", formData.name);
      formParams.append("email", formData.email);

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE}/api/me`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: localStorage.getItem("auth_token"),
        },
        body: formParams,
      });

      if (!userResponse.ok) {
        throw new Error("사용자 정보 업데이트에 실패했습니다.");
      }

      // Create checkout request
      const checkoutResponse = await fetch(`${process.env.REACT_APP_API_BASE}/api/cart/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("auth_token"),
        },
        body: JSON.stringify({
          cart_uuid: cartUuid,
          zipCode: formData.zipCode,
          address: formData.address,
          addressDetail: formData.addressDetail,
          memo: formData.memo || null,
        }),
      });

      if (!checkoutResponse.ok) {
        throw new Error("주문 생성에 실패했습니다.");
      }

      const orderData = await checkoutResponse.json();

      if (orderData.pay_code) {
        // Show processing message
        setIsProcessing(true);

        // 리다이렉트 코드 수정
        console.log("결제 페이지로 리다이렉트:", `${process.env.REACT_APP_PAY_BASE}/${orderData.pay_code}`);

        // setTimeout을 사용하여 상태 업데이트 후 리다이렉트 실행
        setTimeout(() => {
          try {
            // 환경 변수 확인
            if (!process.env.REACT_APP_PAY_BASE) {
              throw new Error("결제 URL이 설정되지 않았습니다.");
            }

            // 리다이렉트 실행
            window.location.href = `${process.env.REACT_APP_PAY_BASE}/${orderData.pay_code}`;
          } catch (err) {
            console.error("리다이렉트 오류:", err);
            setError("결제 페이지로 이동하는 중 오류가 발생했습니다.");
            setIsProcessing(false);
            setIsSubmitting(false);
          }
        }, 500);
      } else {
        throw new Error("결제 정보를 받아오지 못했습니다.");
      }
    } catch (err) {
      console.error("주문 제출 오류:", err);
      setError(err.message || "주문 처리 중 오류가 발생했습니다.");
      setIsSubmitting(false);
    }
  };

  // Format price with commas
  const formatPrice = (price) => {
    return price.toLocaleString() + "원";
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-teal-100 mb-4">
              <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">주문이 완료되었습니다!</h3>
            <p className="text-sm text-gray-500 mb-6">
              결제 확인 이메일이 {formData.email || "귀하의 이메일"}로 전송되었습니다.<br></br>장바구니 우측 상단의 내 메뉴에서 주문내역을 확인할 수 있습니다.
            </p>
            <button onClick={onClose} className="w-full bg-teal-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-teal-600">
              확인
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-teal-50 mb-4">
              <svg className="animate-spin h-6 w-6 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">결제 처리 중...</h3>
            <p className="text-sm text-gray-500">잠시만 기다려주세요. 결제 페이지로 이동합니다.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-teal-50 mb-4">
              <svg className="animate-spin h-6 w-6 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">정보를 불러오는 중...</h3>
            <p className="text-sm text-gray-500">잠시만 기다려주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">주문 정보 입력</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 rounded-lg text-red-700 border border-red-200 text-sm">{error}</div>}

        <div className="grid grid-cols-1 gap-6 ">
          {/* 배송 정보 */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-4">
              <Truck className="w-5 h-5 text-teal-500 mr-2" />
              <h3 className="text-lg font-medium">배송 정보</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                  {/* <input type="tel" name="phone" value={formData.phone} onChange={handleChange} readonly required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" /> */}
                  <input type="tel" name="phone" value={formData.phone} readonly required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">우편번호</label>
                  <div className="flex">
                    <input type="text" name="zipCode" value={formData.zipCode} readOnly required className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-100" placeholder="우편번호" />
                    <button type="button" onClick={handleAddressSearch} className="ml-2 px-3 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600">
                      주소 찾기
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                  <input type="text" name="address" value={formData.address} readOnly required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상세주소</label>
                  <input type="text" name="addressDetail" value={formData.addressDetail} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="상세주소를 입력해주세요" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">주문 메모</label>
                  <textarea name="memo" value={formData.memo} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" rows="2" placeholder="제작 및 배송 시 참고사항이나 요청사항을 입력해주세요" />
                </div>
              </div>
            </form>
          </div>

          {/* 주문 요약 */}
          {cartDetails && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-4">주문 요약</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>견적 금액</span>
                  <span>{formatPrice(cartDetails.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>배송비</span>
                  <span>{cartDetails.shipping_fee === 0 ? "무료" : formatPrice(cartDetails.shipping_fee)}</span>
                </div>
                <div className="flex justify-between">
                  <span>추가 서비스 비용</span>
                  <span>{formatPrice(cartDetails.additional_fee)}</span>
                </div>
                {cartDetails.applied_coupon && (
                  <div className="mt-2">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center">
                        <span className="text-teal-500 font-semibold">{cartDetails.applied_coupon.couponName}</span>
                      </div>
                      <span className="text-teal-500 font-semibold">{cartDetails.applied_coupon.type === "PERCENTAGE" ? `-${cartDetails.applied_coupon.discountAmount}%` : `-${cartDetails.applied_coupon.discountAmount.toLocaleString()}원`}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 flex justify-between">
                      <span>쿠폰ID: {cartDetails.applied_coupon.couponId}</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>총 결제 금액</span>
                  <span className="text-teal-500 font-bold text-base">{formatPrice(cartDetails.total_cost)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 mr-2">
            취소
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting} className="px-6 py-2 bg-teal-500 text-white rounded-md font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center">
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                처리 중...
              </>
            ) : (
              "결제하기"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;
